import { alpha, createTheme } from '@mui/material/styles';

const bodyFontFamily = '"Manrope", "Roboto", "Helvetica", "Arial", sans-serif';
const displayFontFamily = '"Space Grotesk", "Manrope", "Roboto", sans-serif';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#76F4B8',
      light: '#B9FFD8',
      dark: '#18A86C',
      contrastText: '#04120C',
    },
    secondary: {
      main: '#FFD166',
      light: '#FFE5A3',
      dark: '#B57B10',
      contrastText: '#1B1300',
    },
    success: {
      main: '#54E38D',
      light: '#A7F5C1',
      dark: '#0D9650',
      contrastText: '#04120C',
    },
    error: {
      main: '#FF6B7A',
      light: '#FFA8B1',
      dark: '#B8213F',
      contrastText: '#2B030A',
    },
    warning: {
      main: '#FFB74D',
      light: '#FFD59A',
      dark: '#A95F00',
      contrastText: '#1B1300',
    },
    info: {
      main: '#7CC7FF',
      light: '#B7E0FF',
      dark: '#0D75B9',
      contrastText: '#03111D',
    },
    background: {
      default: '#090D0E',
      paper: '#101715',
    },
    text: {
      primary: '#F4FFF9',
      secondary: 'rgba(244, 255, 249, 0.68)',
    },
    action: {
      active: 'rgba(244, 255, 249, 0.84)',
      hover: 'rgba(244, 255, 249, 0.08)',
      selected: 'rgba(118, 244, 184, 0.16)',
      disabled: 'rgba(244, 255, 249, 0.58)',
      disabledBackground: 'rgba(244, 255, 249, 0.12)',
      focus: 'rgba(118, 244, 184, 0.22)',
    },
    divider: 'rgba(226, 255, 238, 0.12)',
  },
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: bodyFontFamily,
    allVariants: {
      letterSpacing: 0,
    },
    h1: {
      fontFamily: displayFontFamily,
      fontWeight: 700,
      lineHeight: 1.04,
    },
    h2: {
      fontFamily: displayFontFamily,
      fontWeight: 700,
      lineHeight: 1.04,
    },
    h3: {
      fontFamily: displayFontFamily,
      fontWeight: 700,
      lineHeight: 1.05,
    },
    h4: {
      fontFamily: displayFontFamily,
      fontWeight: 700,
      lineHeight: 1.05,
    },
    h5: {
      fontFamily: displayFontFamily,
      fontWeight: 700,
      lineHeight: 1.1,
    },
    h6: {
      fontFamily: displayFontFamily,
      fontWeight: 700,
      lineHeight: 1.2,
    },
    subtitle1: {
      fontWeight: 700,
    },
    button: {
      fontWeight: 800,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          minHeight: '100%',
          backgroundColor: '#090D0E',
        },
        body: {
          width: '100%',
          minHeight: '100%',
          height: 'var(--app-viewport-stable-height, 100dvh)',
          margin: 0,
          overflow: 'hidden',
          position: 'fixed',
          backgroundColor: '#090D0E',
          backgroundImage:
            'linear-gradient(145deg, rgba(118, 244, 184, 0.10) 0%, rgba(118, 244, 184, 0) 36%), linear-gradient(315deg, rgba(255, 209, 102, 0.10) 0%, rgba(255, 209, 102, 0) 32%)',
          backgroundAttachment: 'fixed',
        },
        '#root': {
          height: 'var(--app-viewport-stable-height, 100dvh)',
          minHeight: 'var(--app-viewport-stable-height, 100dvh)',
          overflow: 'hidden',
        },
        ':root': {
          '--app-tg-safe-area-inset-top': 'var(--tg-safe-area-inset-top, 0px)',
          '--app-tg-safe-area-inset-right': 'var(--tg-safe-area-inset-right, 0px)',
          '--app-tg-safe-area-inset-bottom': 'var(--tg-safe-area-inset-bottom, 0px)',
          '--app-tg-safe-area-inset-left': 'var(--tg-safe-area-inset-left, 0px)',
          '--app-tg-content-safe-area-inset-top':
            'var(--tg-content-safe-area-inset-top, 0px)',
          '--app-tg-content-safe-area-inset-right':
            'var(--tg-content-safe-area-inset-right, 0px)',
          '--app-tg-content-safe-area-inset-bottom':
            'var(--tg-content-safe-area-inset-bottom, 0px)',
          '--app-tg-content-safe-area-inset-left':
            'var(--tg-content-safe-area-inset-left, 0px)',
          '--app-safe-area-top':
            'max(var(--app-tg-safe-area-inset-top), var(--app-tg-content-safe-area-inset-top), env(safe-area-inset-top, 0px))',
          '--app-safe-area-right':
            'max(var(--app-tg-safe-area-inset-right), var(--app-tg-content-safe-area-inset-right), env(safe-area-inset-right, 0px))',
          '--app-safe-area-bottom':
            'max(var(--app-tg-safe-area-inset-bottom), var(--app-tg-content-safe-area-inset-bottom), env(safe-area-inset-bottom, 0px))',
          '--app-safe-area-left':
            'max(var(--app-tg-safe-area-inset-left), var(--app-tg-content-safe-area-inset-left), env(safe-area-inset-left, 0px))',
          '--app-viewport-height': 'var(--tg-viewport-height, 100dvh)',
          '--app-viewport-stable-height': 'var(--tg-viewport-stable-height, 100dvh)',
          '--app-ios-extra-offset': '0px',
          '--app-content-max-width': '600px',
          '--app-page-inline-padding': '16px',
          '--app-page-top-padding':
            'calc(20px + var(--app-tg-content-safe-area-inset-top) + var(--app-ios-extra-offset) + 2.5vh)',
          '--app-page-bottom-padding': '20px',
          '--app-bottom-nav-height': '62px',
          '--app-bottom-nav-gap': 'clamp(10px, 1.5vh, 18px)',
          '--app-bottom-obstruction':
            'calc(var(--app-bottom-nav-height) + var(--app-bottom-nav-gap) + var(--app-safe-area-bottom))',
        },
        '::selection': {
          backgroundColor: alpha('#76F4B8', 0.35),
        },
        'input[type="number"]': {
          MozAppearance: 'textfield',
        },
        'input[type="number"]::-webkit-outer-spin-button, input[type="number"]::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid rgba(226, 255, 238, 0.12)',
          backgroundColor: alpha('#121B18', 0.86),
          backgroundImage:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.025))',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.28)',
          backdropFilter: 'blur(18px)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: 'none',
          minHeight: 42,
          boxShadow: 'none',
          transition:
            'transform 180ms cubic-bezier(0.2, 0, 0, 1), background-color 180ms ease, border-color 180ms ease',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
          '&.Mui-disabled': {
            transform: 'none',
          },
        },
        contained: {
          backgroundImage: 'none',
          '&.Mui-disabled': {
            backgroundImage: 'none',
            backgroundColor: alpha('#F4FFF9', 0.12),
            color: alpha('#F4FFF9', 0.58),
            border: '1px solid rgba(226, 255, 238, 0.10)',
          },
        },
        containedPrimary: {
          backgroundImage: 'linear-gradient(135deg, #76F4B8, #FFD166)',
          color: '#06120D',
          '&:hover': {
            backgroundImage: 'linear-gradient(135deg, #A7F5C1, #FFE5A3)',
          },
        },
        containedSecondary: {
          backgroundColor: '#FFD166',
          color: '#1B1300',
          '&:hover': {
            backgroundColor: '#FFE5A3',
          },
        },
        containedSuccess: {
          backgroundColor: '#54E38D',
          color: '#04120C',
          '&:hover': {
            backgroundColor: '#A7F5C1',
          },
        },
        containedError: {
          backgroundColor: '#FF6B7A',
          color: '#2B030A',
          '&:hover': {
            backgroundColor: '#FFA8B1',
          },
        },
        containedWarning: {
          backgroundColor: '#FFB74D',
          color: '#1B1300',
          '&:hover': {
            backgroundColor: '#FFD59A',
          },
        },
        containedInfo: {
          backgroundColor: '#7CC7FF',
          color: '#03111D',
          '&:hover': {
            backgroundColor: '#B7E0FF',
          },
        },
        outlined: {
          borderColor: 'rgba(226, 255, 238, 0.18)',
          backgroundColor: alpha('#F4FFF9', 0.03),
          '&:hover': {
            borderColor: 'rgba(118, 244, 184, 0.52)',
            backgroundColor: alpha('#76F4B8', 0.08),
          },
          '&.Mui-disabled': {
            color: alpha('#F4FFF9', 0.58),
            borderColor: 'rgba(226, 255, 238, 0.16)',
            backgroundColor: alpha('#F4FFF9', 0.045),
          },
        },
        outlinedError: {
          '&:hover': {
            borderColor: '#FF6B7A',
            backgroundColor: alpha('#FF6B7A', 0.12),
          },
        },
        text: {
          '&.Mui-disabled': {
            color: alpha('#F4FFF9', 0.58),
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          transition:
            'transform 180ms cubic-bezier(0.2, 0, 0, 1), background-color 180ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            backgroundColor: alpha('#F4FFF9', 0.08),
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backgroundColor: alpha('#F4FFF9', 0.045),
          transition: 'background-color 180ms ease, box-shadow 180ms ease',
          '&:hover': {
            backgroundColor: alpha('#F4FFF9', 0.065),
          },
          '&.Mui-focused': {
            backgroundColor: alpha('#F4FFF9', 0.075),
            boxShadow: `0 0 0 4px ${alpha('#76F4B8', 0.12)}`,
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(226, 255, 238, 0.14)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(226, 255, 238, 0.24)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#76F4B8',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 800,
          letterSpacing: 0,
        },
        filled: {
          '& .MuiChip-icon': {
            color: 'inherit',
          },
        },
        outlined: {
          borderColor: 'rgba(226, 255, 238, 0.16)',
          backgroundColor: alpha('#F4FFF9', 0.025),
          '& .MuiChip-icon': {
            color: 'inherit',
          },
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#0E1714', 0.86),
          border: '1px solid rgba(226, 255, 238, 0.14)',
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: 'rgba(244, 255, 249, 0.62)',
          minWidth: 0,
          transition: 'color 180ms ease, transform 180ms cubic-bezier(0.2, 0, 0, 1)',
          '&.Mui-selected': {
            color: '#76F4B8',
            transform: 'translateY(-2px)',
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '0.72rem',
            fontWeight: 800,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 44,
        },
        indicator: {
          display: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 999,
          textTransform: 'none',
          fontWeight: 800,
          color: 'rgba(244, 255, 249, 0.68)',
          '&.Mui-selected': {
            color: '#06120D',
            backgroundColor: '#76F4B8',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid rgba(226, 255, 238, 0.12)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: 'rgba(244, 255, 249, 0.72)',
          fontWeight: 800,
          borderColor: 'rgba(226, 255, 238, 0.12)',
        },
        body: {
          borderColor: 'rgba(226, 255, 238, 0.08)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          border: '1px solid rgba(226, 255, 238, 0.16)',
          backgroundColor: alpha('#101715', 0.96),
          backgroundImage:
            'linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02))',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          backgroundColor: alpha('#F4FFF9', 0.08),
        },
        bar: {
          borderRadius: 999,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid rgba(226, 255, 238, 0.12)',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid rgba(226, 255, 238, 0.12)',
          backgroundColor: alpha('#121B18', 0.76),
          backgroundImage: 'none',
          '&:before': {
            display: 'none',
          },
          '& + &': {
            marginTop: 8,
          },
        },
      },
    },
  },
});

export default theme;
